import React, { useState, useEffect, useRef } from 'react';

const GardenMode = ({ audioContext, isSoundEnabled }) => {
  const [flowers, setFlowers] = useState([]);
  const [butterflies, setButterflies] = useState([]);
  const [raindrops, setRaindrops] = useState([]);
  const [sunrays, setSunrays] = useState([]);
  const [isRaining, setIsRaining] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [lastRClickTime, setLastRClickTime] = useState(0);
  const [rClickCount, setRClickCount] = useState(0);
  const animFrameRef = useRef(null);
  const rainIntervalRef = useRef(null);
  const hintTimerRef = useRef(null);
  const handlersRef = useRef({});

  useEffect(() => {
    hintTimerRef.current = setTimeout(() => setShowHint(false), 6000);
    return () => clearTimeout(hintTimerRef.current);
  }, []);

  // ─── AUDIO ─────────────────────────────────────────────────────────────────
  const playFlowerBloom = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    // Warm harp-like chord
    const notes = [261.63, 329.63, 392, 523.25];
    notes.forEach((freq, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      osc.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
      filter.type = 'lowpass'; filter.frequency.value = 1200;
      osc.type = 'triangle'; osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + i * 0.07);
      gain.gain.linearRampToValueAtTime(0.06, now + i * 0.07 + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.07 + 1.4);
      osc.start(now + i * 0.07); osc.stop(now + i * 0.07 + 1.5);
    });
    // Warm sub
    const sub = audioContext.createOscillator(); const subGain = audioContext.createGain();
    sub.connect(subGain); subGain.connect(audioContext.destination);
    sub.type = 'sine'; sub.frequency.value = 65;
    subGain.gain.setValueAtTime(0, now); subGain.gain.linearRampToValueAtTime(0.04, now + 0.1);
    subGain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    sub.start(now); sub.stop(now + 0.7);
  };

  const playButterflySound = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    // Gentle shimmer arpeggio — fairy-dust chime
    [523.25, 659.26, 783.99, 1046.5].forEach((freq, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      osc.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
      filter.type = 'lowpass'; filter.frequency.value = 4000;
      osc.type = 'sine'; osc.frequency.value = freq;
      const t = now + i * 0.065;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.026, t + 0.025);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.85);
      osc.start(t); osc.stop(t + 0.9);
    });
    // Soft warm sub
    const sub = audioContext.createOscillator(); const sg = audioContext.createGain();
    sub.connect(sg); sg.connect(audioContext.destination);
    sub.type = 'triangle'; sub.frequency.value = 261.63;
    sg.gain.setValueAtTime(0, now); sg.gain.linearRampToValueAtTime(0.02, now + 0.04);
    sg.gain.exponentialRampToValueAtTime(0.001, now + 0.65);
    sub.start(now); sub.stop(now + 0.7);
  };

  const playSunraySound = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    // Warm rising arpeggio (major)
    const notes = [261.63, 329.63, 392, 523.25, 659.26, 783.99];
    notes.forEach((freq, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      osc.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
      filter.type = 'lowpass'; filter.frequency.value = 1400;
      osc.type = 'sine'; osc.frequency.value = freq;
      const t = now + i * 0.12;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.07, t + 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 2.0);
      osc.start(t); osc.stop(t + 2.1);
    });
  };

  const playRainSound = (active) => {
    if (!audioContext || !isSoundEnabled) return;
    if (active) {
      if (audioContext.state === 'suspended') audioContext.resume();
      const now = audioContext.currentTime;
      const sr = audioContext.sampleRate;
      // Layer 1: Background rain rumble (lowpass)
      const bufL1 = audioContext.createBuffer(1, sr * 3, sr);
      const dL1 = bufL1.getChannelData(0);
      for (let i = 0; i < sr * 3; i++) dL1[i] = Math.random() * 2 - 1;
      const srcL1 = audioContext.createBufferSource();
      srcL1.buffer = bufL1; srcL1.loop = true;
      const fL1 = audioContext.createBiquadFilter();
      fL1.type = 'lowpass'; fL1.frequency.value = 500;
      const gL1 = audioContext.createGain();
      gL1.gain.setValueAtTime(0, now); gL1.gain.linearRampToValueAtTime(0.13, now + 0.9);
      srcL1.connect(fL1); fL1.connect(gL1); gL1.connect(audioContext.destination);
      srcL1.start(now);
      // Layer 2: Mid droplets (bandpass ~3kHz)
      const bufL2 = audioContext.createBuffer(1, sr * 2, sr);
      const dL2 = bufL2.getChannelData(0);
      for (let i = 0; i < sr * 2; i++) dL2[i] = Math.random() * 2 - 1;
      const srcL2 = audioContext.createBufferSource();
      srcL2.buffer = bufL2; srcL2.loop = true;
      const fL2 = audioContext.createBiquadFilter();
      fL2.type = 'bandpass'; fL2.frequency.value = 2800; fL2.Q.value = 0.5;
      const gL2 = audioContext.createGain();
      gL2.gain.setValueAtTime(0, now); gL2.gain.linearRampToValueAtTime(0.1, now + 0.6);
      srcL2.connect(fL2); fL2.connect(gL2); gL2.connect(audioContext.destination);
      srcL2.start(now);
      // Layer 3: Surface hiss (highpass >5kHz)
      const bufL3 = audioContext.createBuffer(1, sr * 2, sr);
      const dL3 = bufL3.getChannelData(0);
      for (let i = 0; i < sr * 2; i++) dL3[i] = Math.random() * 2 - 1;
      const srcL3 = audioContext.createBufferSource();
      srcL3.buffer = bufL3; srcL3.loop = true;
      const fL3 = audioContext.createBiquadFilter();
      fL3.type = 'highpass'; fL3.frequency.value = 5000;
      const gL3 = audioContext.createGain();
      gL3.gain.setValueAtTime(0, now); gL3.gain.linearRampToValueAtTime(0.055, now + 0.7);
      srcL3.connect(fL3); fL3.connect(gL3); gL3.connect(audioContext.destination);
      srcL3.start(now);
      window._gardenRainSrc = { nodes: [srcL1, srcL2, srcL3], gains: [gL1, gL2, gL3] };
    } else {
      if (window._gardenRainSrc) {
        const { nodes, gains } = window._gardenRainSrc;
        const now = audioContext.currentTime;
        gains.forEach(g => {
          g.gain.cancelScheduledValues(now);
          g.gain.setValueAtTime(g.gain.value, now);
          g.gain.linearRampToValueAtTime(0, now + 0.5);
        });
        setTimeout(() => { try { nodes.forEach(n => n.stop()); } catch(e){} window._gardenRainSrc = null; }, 600);
      }
    }
  };

  // ─── ANIMATION LOOP ────────────────────────────────────────────────────────
  useEffect(() => {
    const tick = () => {
      setFlowers(prev => prev.map(f => ({
        ...f,
        rotation: ((f.rotation || 0) + 0.04) % 360,
        currentHue: (f.hue || 0) + Math.sin(Date.now() / 2500 + (f.id % 100)) * 22
      })));

      setButterflies(prev => prev.map(b => {
        const age = Date.now() - b.born;
        if (age > 12000) return null;
        const wx = Math.cos(age / (b.freqX || 900) + b.phase) * (b.ampX || 1.2) + (b.driftX || 0);
        const wy = Math.sin(age / (b.freqY || 700) + b.phase * 0.7) * (b.ampY || 0.9) + (b.driftY || 0);
        let tx = b.x + wx;
        let ty = b.y + wy;
        // Attracted to nearby flowers
        if (b.targetFlower) {
          tx += (b.targetFlower.x - b.x) * 0.002;
          ty += (b.targetFlower.y - b.y) * 0.002;
        }
        return { ...b, x: tx, y: ty, wingPhase: age / 80, opacity: Math.min(1, Math.max(0, 1 - (age - 10000) / 2000)) };
      }).filter(Boolean));

      setRaindrops(prev => prev
        .map(r => ({ ...r, y: r.y + r.speed, x: r.x + r.drift }))
        .filter(r => r.y < window.innerHeight + 20)
      );

      setSunrays(prev => prev.map(r => {
        const age = Date.now() - r.born;
        return { ...r, opacity: Math.max(0, 1 - age / 3000), length: Math.min(r.maxLength, r.length + 4) };
      }).filter(r => r.opacity > 0));

      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, []);

  // Rain spawner
  useEffect(() => {
    if (isRaining) {
      rainIntervalRef.current = setInterval(() => {
        const count = 8;
        setRaindrops(prev => [...prev, ...Array.from({ length: count }).map((_, i) => ({
          id: Date.now() + i,
          x: Math.random() * window.innerWidth,
          y: -10,
          speed: 4 + Math.random() * 3,
          drift: (Math.random() - 0.5) * 0.5,
          len: 8 + Math.random() * 10,
          opacity: 0.4 + Math.random() * 0.4
        }))]);
      }, 40);
    } else {
      clearInterval(rainIntervalRef.current);
    }
    return () => clearInterval(rainIntervalRef.current);
  }, [isRaining]);

  // ─── INTERACTION ──────────────────────────────────────────────────────────
  handlersRef.current = {
    handleMouseDown: (e) => {
      if (e.button === 0) {
        // Spawn butterfly
        const hue = Math.floor(Math.random() * 360);
        setButterflies(prev => [...prev, {
          id: Date.now() + Math.random(),
          x: e.clientX, y: e.clientY,
          hue, phase: Math.random() * Math.PI * 2,
          born: Date.now(), wingPhase: 0, opacity: 1,
          size: 14 + Math.random() * 10,
          freqX: 550 + Math.random() * 700,
          freqY: 420 + Math.random() * 500,
          ampX: 0.7 + Math.random() * 1.3,
          ampY: 0.5 + Math.random() * 1.0,
          driftX: (Math.random() - 0.5) * 0.5,
          driftY: (Math.random() - 0.5) * 0.3,
          targetFlower: flowers[Math.floor(Math.random() * flowers.length)] || null
        }]);
        setShowHint(false);
        playButterflySound();
      }
      if (e.button === 2) {
        const now = Date.now();
        const tSince = now - lastRClickTime;
        if (tSince < 350 && rClickCount === 1) {
          // Double right-click: sunray
          const rayCount = 8;
          const newRays = Array.from({ length: rayCount }).map((_, i) => ({
            id: Date.now() + i,
            x: e.clientX, y: e.clientY,
            angle: (360 / rayCount) * i,
            maxLength: 200 + Math.random() * 80,
            length: 0, born: Date.now(), opacity: 1
          }));
          setSunrays(prev => [...prev, ...newRays]);
          playSunraySound();
          setRClickCount(0);
        } else {
          setRClickCount(1);
          setTimeout(() => setRClickCount(0), 350);
          // Single right-click: flower
          const petalCount = 5 + Math.floor(Math.random() * 6);
          const hue = Math.floor(Math.random() * 360);
          const flowerSize = 18 + Math.random() * 20;
          setFlowers(prev => [...prev, {
            id: Date.now() + Math.random(),
            x: e.clientX, y: e.clientY,
            hue, petalCount, size: flowerSize,
            born: Date.now(), bright: false,
            rotation: Math.random() * 360, currentHue: hue
          }]);
          playFlowerBloom();
        }
        setLastRClickTime(now);
        setShowHint(false);
      }
      if (e.button === 1) {
        e.preventDefault();
        setIsRaining(true);
        playRainSound(true);
        // Brighten flowers
        setFlowers(prev => prev.map(f => ({ ...f, bright: true })));
      }
    },
    handleMouseUp: (e) => {
      if (e.button === 1) {
        setIsRaining(false);
        playRainSound(false);
        setTimeout(() => setFlowers(prev => prev.map(f => ({ ...f, bright: false }))), 800);
      }
    }
  };

  useEffect(() => {
    const down = (e) => handlersRef.current.handleMouseDown(e);
    const up = (e) => handlersRef.current.handleMouseUp(e);
    const ctx = (e) => e.preventDefault();
    window.addEventListener('mousedown', down);
    window.addEventListener('mouseup', up);
    window.addEventListener('contextmenu', ctx);
    return () => {
      window.removeEventListener('mousedown', down);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('contextmenu', ctx);
    };
  }, []);

  const renderFlower = (f) => {
    const sat = f.bright ? 90 : 75;
    const light = f.bright ? 68 : 58;
    const hue = f.currentHue !== undefined ? f.currentHue : f.hue;
    return (
      <g key={f.id} transform={`translate(${f.x}, ${f.y}) rotate(${f.rotation || 0})`}
         style={{ filter: f.bright ? `drop-shadow(0 0 ${f.size*0.4}px hsla(${hue},90%,70%,0.7))` : 'none' }}>
        {Array.from({ length: f.petalCount }).map((_, i) => {
          const angle = (360 / f.petalCount) * i;
          return (
            <ellipse key={i}
              cx={Math.cos(angle * Math.PI / 180) * f.size * 0.7}
              cy={Math.sin(angle * Math.PI / 180) * f.size * 0.7}
              rx={f.size * 0.35} ry={f.size * 0.22}
              fill={`hsla(${hue + i * 10}, ${sat}%, ${light}%, 0.88)`}
              transform={`rotate(${angle}, ${Math.cos(angle * Math.PI / 180) * f.size * 0.7}, ${Math.sin(angle * Math.PI / 180) * f.size * 0.7})`}
            />
          );
        })}
        <circle r={f.size * 0.28} fill={`hsla(${hue + 40}, 80%, 75%, 0.95)`} />
        <circle r={f.size * 0.14} fill={`hsla(${hue + 60}, 60%, 40%, 0.8)`} />
      </g>
    );
  };

  const renderButterfly = (b) => {
    const wing = Math.abs(Math.sin(b.wingPhase)) * 30;
    return (
      <g key={b.id} transform={`translate(${b.x}, ${b.y})`} opacity={b.opacity}>
        {/* Upper wings */}
        <ellipse cx={-b.size * 0.5} cy={-b.size * 0.2} rx={b.size * 0.7} ry={b.size * 0.5}
          fill={`hsla(${b.hue}, 80%, 65%, 0.85)`}
          transform={`skewX(${-wing})`} />
        <ellipse cx={b.size * 0.5} cy={-b.size * 0.2} rx={b.size * 0.7} ry={b.size * 0.5}
          fill={`hsla(${b.hue + 20}, 75%, 60%, 0.85)`}
          transform={`skewX(${wing})`} />
        {/* Lower wings */}
        <ellipse cx={-b.size * 0.4} cy={b.size * 0.2} rx={b.size * 0.45} ry={b.size * 0.35}
          fill={`hsla(${b.hue + 40}, 85%, 55%, 0.75)`}
          transform={`skewX(${-wing * 0.7})`} />
        <ellipse cx={b.size * 0.4} cy={b.size * 0.2} rx={b.size * 0.45} ry={b.size * 0.35}
          fill={`hsla(${b.hue + 60}, 80%, 55%, 0.75)`}
          transform={`skewX(${wing * 0.7})`} />
        {/* Body */}
        <ellipse rx={b.size * 0.08} ry={b.size * 0.4} fill={`hsla(${b.hue}, 40%, 25%, 0.9)`} />
        {/* Antennae */}
        <path d={`M0,${-b.size*0.4} Q${-b.size*0.3},${-b.size*0.8} ${-b.size*0.2},${-b.size*0.9}`}
          fill="none" stroke={`hsla(${b.hue}, 40%, 30%, 0.7)`} strokeWidth="0.8" />
        <path d={`M0,${-b.size*0.4} Q${b.size*0.3},${-b.size*0.8} ${b.size*0.2},${-b.size*0.9}`}
          fill="none" stroke={`hsla(${b.hue}, 40%, 30%, 0.7)`} strokeWidth="0.8" />
      </g>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', zIndex: 99990, pointerEvents: 'none' }}>
      <style>{`
        @keyframes hintFadeIn {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Rain overlay */}
      {isRaining && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(80,120,180,0.06)', transition: 'all 0.5s', pointerEvents: 'none' }} />
      )}

      <svg style={{ position: 'absolute', width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
        {/* Flowers */}
        {flowers.map(renderFlower)}
        {/* Butterflies */}
        {butterflies.map(renderButterfly)}
        {/* Raindrops */}
        {raindrops.map(r => (
          <line key={r.id} x1={r.x} y1={r.y} x2={r.x - r.drift * 2} y2={r.y - r.len}
            stroke={`rgba(140, 190, 255, ${r.opacity})`} strokeWidth="1" strokeLinecap="round" />
        ))}
        {/* Sunrays */}
        {sunrays.map(r => {
          const rad = r.angle * Math.PI / 180;
          return (
            <line key={r.id} style={{ display: 'none' }} />
          );
        })}
      </svg>
      {/* Sunrays - volumetric cones */}
      <svg style={{ position: 'absolute', width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
        <defs>
          {sunrays.map(r => {
            const rad = r.angle * Math.PI / 180;
            return (
              <linearGradient key={`sg${r.id}`} id={`sg${r.id}`} gradientUnits="userSpaceOnUse"
                x1={r.x} y1={r.y}
                x2={r.x + Math.cos(rad) * r.length}
                y2={r.y + Math.sin(rad) * r.length}>
                <stop offset="0%" stopColor={`rgba(255,215,60,${r.opacity * 0.7})`}/>
                <stop offset="65%" stopColor={`rgba(255,232,120,${r.opacity * 0.22})`}/>
                <stop offset="100%" stopColor="rgba(255,248,180,0)"/>
              </linearGradient>
            );
          })}
        </defs>
        <g style={{ filter: 'blur(3px)' }}>
          {sunrays.map(r => {
            const rad = r.angle * Math.PI / 180;
            const tipX = r.x + Math.cos(rad) * r.length;
            const tipY = r.y + Math.sin(rad) * r.length;
            const perpRad = rad + Math.PI / 2;
            const coneW = r.maxLength * 0.1;
            return (
              <polygon key={r.id}
                points={`${r.x + Math.cos(perpRad)*coneW},${r.y + Math.sin(perpRad)*coneW} ${r.x - Math.cos(perpRad)*coneW},${r.y - Math.sin(perpRad)*coneW} ${tipX},${tipY}`}
                fill={`url(#sg${r.id})`}
              />
            );
          })}
        </g>
      </svg>

      {showHint && (
        <div style={{
          position: 'fixed', bottom: '160px', left: '50%', transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, rgba(40,80,20,0.92), rgba(80,140,40,0.92))',
          backdropFilter: 'blur(16px)', borderRadius: '16px', padding: '14px 22px',
          border: '1px solid rgba(160,230,100,0.3)',
          color: 'rgba(220,255,180,0.95)', fontSize: '13px',
          animation: 'hintFadeIn 0.4s ease', pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 99999
        }}>
          🌸 ЛКМ — бабочки · ПКМ — цветы · ПКМ×2 — солнечный луч · Wheel — дождь
        </div>
      )}
    </div>
  );
};

export default GardenMode;
